import { faChevronDown, faChevronUp } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { LessonCard } from './LessonCard';
import { ScrollableCardsGrid } from './ScrollableCardsGrid';
import type { Course as CourseType } from './types';

export function Course({ course }: { course: CourseType }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);

  return (
    <CourseWrapper>
      <HeaderWrapper>
        <CourseHeader>{course.title}</CourseHeader>
        <IconWrapper onClick={() => setIsOpen(!isOpen)}>
          <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
        </IconWrapper>
      </HeaderWrapper>

      <SectionDescription>
        {course.description}{' '}
        <DetailLinkWrapper>
          <Link to="/courses/$courseSlug/" params={{ courseSlug: course.slug }}>
            {t('wbs.see-detail')}
          </Link>
        </DetailLinkWrapper>
      </SectionDescription>

      <LessonsWrapper isOpen={isOpen}>
        {/* The directory payload carries each course's lessons, at the version
            the tile shows, so a tab switch renders them with no request. */}
        <ScrollableCardsGrid>
          {course.lessons.map((lesson) => (
            <LessonCard key={lesson.id} lesson={lesson} />
          ))}
        </ScrollableCardsGrid>
      </LessonsWrapper>
    </CourseWrapper>
  );
}

const CourseHeader = styled.div`
  align-items: flex-start;
  color: var(--grey-1000);
  cursor: pointer;
  display: flex;
  font-family: var(--raleway);
  font-size: 18px;
  font-weight: 600;
  justify-content: space-between;
  margin-right: 8px;
  padding: 0px 0px 4px 4px;
`;

const CourseWrapper = styled.div`
  background: var(--bg-default);
  border: none;
  border-radius: 5px;
  box-shadow: none;
  padding: 20px 6px 8px 6px;
`;

const HeaderWrapper = styled.div`
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
  user-select: none;
`;

const IconWrapper = styled.div`
  align-items: flex-start;
  color: var(--grey-1000);
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  font-size: 0.9em;
  justify-content: flex-end;
  margin: 0 8px 0 12px;
  transition: transform 0.2s ease;

  svg {
    stroke: var(--grey-1000);
    stroke-width: 25;
  }
`;

const LessonsWrapper = styled.div<{ isOpen: boolean }>`
  margin: 21px 0 0 4px;
  max-height: ${({ isOpen }) => (isOpen ? '1000px' : '0')};
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  overflow: hidden;
  transition:
    max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.3s ease;
  will-change: max-height, opacity;
`;

const DetailLinkWrapper = styled.span`
  a {
    color: var(--wbs-red);
    text-decoration: none;
  }
`;

const SectionDescription = styled.p`
  font-family: var(--raleway);
  font-size: 15px;
  font-weight: 400;
  margin: 8px 0 16px 4px;
`;
